import { apiUrl } from './config';
import { ApiError, NetworkError, apiErrorFromResponse } from './errors';
import { tokenStore } from '@/auth/tokenStore';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Skip the bearer header (auth bootstrap). */
  anonymous?: boolean;
  /** Abort signal (React Query passes one). */
  signal?: AbortSignal;
  /** Override timeout (ms). Emit uses a longer one. */
  timeoutMs?: number;
  /** Return raw Response instead of parsed JSON. */
  raw?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Rotate tokens once; concurrent 401s share the same promise. */
export async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const current = tokenStore.get();
    if (!current?.refreshToken) return false;
    try {
      const res = await fetch(apiUrl('/auth/token/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refresh_token: current.refreshToken }),
      });
      if (!res.ok) throw apiErrorFromResponse(res.status, await res.text());
      const json = (await res.json()) as { access_token?: string; refresh_token?: string };
      if (!json.access_token) return false;
      tokenStore.set({
        ...current,
        accessToken: json.access_token,
        refreshToken: json.refresh_token || current.refreshToken,
      });
      return true;
    } catch {
      // The old refresh token is revoked server-side on failure: full logout.
      tokenStore.clear();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function buildQuery(q?: RequestOptions['query']): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function doFetch(path: string, opts: RequestOptions, retryOn401: boolean): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.anonymous) {
    const t = tokenStore.get();
    if (t?.accessToken) headers.Authorization = `Bearer ${t.accessToken}`;
  }

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 30_000);
  const onOuterAbort = () => ctrl.abort();
  opts.signal?.addEventListener('abort', onOuterAbort);

  let res: Response;
  try {
    res = await fetch(apiUrl(path) + buildQuery(opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
    });
  } catch (e) {
    if (opts.signal?.aborted) throw e;
    throw new NetworkError(e);
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onOuterAbort);
  }

  if (res.status === 401 && !opts.anonymous && retryOn401) {
    const ok = await refreshTokens();
    if (ok) return doFetch(path, opts, false);
  }

  if (opts.raw) return res;
  const text = await res.text();
  if (!res.ok) {
    const err = apiErrorFromResponse(res.status, text);
    if (err.isUnauthorized && !opts.anonymous) tokenStore.clear();
    throw err;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** JSON request against `/api/v1` with bearer auth + one-shot refresh on 401. */
export function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return doFetch(path, opts, true) as Promise<T>;
}

export const http = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'DELETE' }),
  request,
};

export { ApiError };
